import React from "react";
import {PageContainer, PageRow} from "@reapptor-apps/reapptor-react-components";
import {ApiProvider, ch} from "@reapptor-apps/reapptor-react-common";
import AuthorizedPage from "@/models/base/AuthorizedPage";
import ServiceProviderModel from "@/models/server/ServiceProviderModel";
import UserContext from "@/models/server/UserContext";
import User from "@/models/server/User";
import InProgress from "@/pages/Mobile/Kyc/InProgress/InProgress";
import {StripeKycState} from "@/models/Enums";
import Verified from "@/pages/Mobile/Kyc/Verified/Verified";
import NotStarted from "@/pages/Mobile/Kyc/NotStarted/NotStarted";
import StripeKycStatus from "@/models/server/stripe/kycstatus/StripeKycStatus";
import Failed from "@/pages/Mobile/Kyc/Failed/Failed";
import WaitingApproval from "@/pages/Mobile/Kyc/WaitingApproval/WaitingApproval";
import AppController from "@/pages/AppController";
import Localizer from "@/localization/Localizer";

import boutStyles from "../../../bout.module.scss";
import styles from "./Kyc.module.scss";

interface IKycProps {
}

interface IKycState {
    user: User | null;
    serviceProvider: ServiceProviderModel | null;
}

export default class Kyc extends AuthorizedPage<IKycProps, IKycState> {

    state: IKycState = {
        user: null,
        serviceProvider: null,
    };

    private async startKycAsync(): Promise<void> {
        const endpoint: string = ((AppController.asCaptain) && (AppController.isEntrepreneur))
            ? "/api/application/getCaptainKycRedirectUrl"
            : "/api/application/getServiceProviderKycRedirectUrl";
        
        window.location.href = await ApiProvider.invokeWithForcedSpinnerAsync(() => this.getAsync<string>(endpoint), true);
    }

    private async fetchMyUser(): Promise<void> {
        const user: User = await this.postAsync("/api/account/me", this.route);
        
        await this.setState({user});
    }

    private async fetchMyServiceProvider(): Promise<void> {
        const context: UserContext | null = ch.findContext();

        const serviceProvider: ServiceProviderModel | null = ((context) && (context.asServiceProviderManager) && (context.user?.serviceProviderId))
            ? await this.postAsync("/api/account/myServiceProvider")
            : null;

        await this.setState({serviceProvider});
    }

    public get user(): User | null {
        return this.state.user;
    }

    public get serviceProvider(): ServiceProviderModel | null {
        return this.state.serviceProvider;
    }

    public get stripeKycStatus(): StripeKycStatus | null {
        return ((AppController.asCaptain) && (AppController.isEntrepreneur))
            ? this.user?.stripeKycStatus ?? null
            : this.serviceProvider?.stripeKycStatus ?? null;
    }

    public getTitle(): string {
        return Localizer.mobileKycPageTitle;
    }

    public async initializeAsync(): Promise<void> {
        await super.initializeAsync();

        await Promise.all([
            this.fetchMyUser(),
            this.fetchMyServiceProvider(),
        ]);
    }

    public render(): React.ReactNode {
        return (
            <PageContainer transparent fullHeight
                           fullWidth={this.mobile}
                           className={this.css(boutStyles.pageContainer, styles.kyc, this.mobile && styles.mobile)}
                           alertClassName={boutStyles.alert}
            >

                <span className={styles.header}>{this.getTitle()}</span>

                {
                    ((this.user) && (this.stripeKycStatus)) &&
                    (
                        <PageRow>

                            {
                                (this.stripeKycStatus.state == StripeKycState.NotStarted) &&
                                (
                                    <NotStarted user={this.user} 
                                                onButtonClick={() => this.startKycAsync()}
                                    />
                                )
                            }

                            {
                                (this.stripeKycStatus.state == StripeKycState.InProgress) &&
                                (
                                    <InProgress stripeKycStatus={this.stripeKycStatus} 
                                                onButtonClick={() => this.startKycAsync()}
                                    />
                                )
                            }

                            {
                                (this.stripeKycStatus.state == StripeKycState.Failed) &&
                                (
                                    <Failed stripeKycStatus={this.stripeKycStatus} 
                                            onButtonClick={() => this.startKycAsync()}
                                    />
                                )
                            }

                            {
                                (this.stripeKycStatus.state == StripeKycState.WaitingForApproval) &&
                                (
                                    <WaitingApproval stripeKycStatus={this.stripeKycStatus} 
                                                     onModifyButtonClick={() => this.startKycAsync()} 
                                                     onRefreshUserButtonClick={() => this.fetchMyUser()}
                                    />
                                )
                            }

                            {
                                (this.stripeKycStatus.state == StripeKycState.Verified) &&
                                (
                                    <Verified onModify={() => this.startKycAsync()}/>
                                )
                            }

                        </PageRow>
                    )
                }

            </PageContainer>
        );
    }
}